import { useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import {
  Autocomplete,
  GoogleMap,
  LoadScript,
  Marker,
} from "@react-google-maps/api";
import { serviceKeys } from "../../constants";
import "react-quill-new/dist/quill.snow.css";
import ReactQuill from "react-quill-new";

const libraries = ["places"];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ align: [] }],
    ["link"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

// ── Inner component — hooks always called, mounts fresh each open ──
const ModalContent = ({ mode, initialData, onClose, onSubmit, loading }) => {
  // Lazy initializer runs once on mount — Quill gets the correct value immediately
  const [form, setForm] = useState(() => ({
    name:          initialData?.name          || "",
    title:         initialData?.title         || "",
    info:          initialData?.info          || "",
    map_latitude:  initialData?.map_latitude  || "",
    map_longitude: initialData?.map_longitude || "",
    map_address:   initialData?.map_address   || "",
    flyer:         initialData?.flyer         || "",
    link:          initialData?.whatsapp_link || "",
  }));

  const [autocomplete, setAutocomplete] = useState(null);

  const center = {
    lat: Number(form.map_latitude) || 40.416775,
    lng: Number(form.map_longitude) || -3.70379,
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {mode === "edit" ? "Edit Landing Page" : "Add Landing Page"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          {/* Info — rich text editor */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">Info</label>
            <div className="rounded-lg overflow-hidden">
              <style>{`
                .landing-page-quill .ql-toolbar {
                  background: #1e2a45;
                  border-color: rgba(255,255,255,0.1) !important;
                  border-radius: 8px 8px 0 0;
                }
                .landing-page-quill .ql-toolbar .ql-stroke { stroke: #94a3b8; }
                .landing-page-quill .ql-toolbar .ql-fill { fill: #94a3b8; }
                .landing-page-quill .ql-toolbar .ql-picker { color: #94a3b8; }
                .landing-page-quill .ql-toolbar button:hover .ql-stroke,
                .landing-page-quill .ql-toolbar button.ql-active .ql-stroke { stroke: #fff; }
                .landing-page-quill .ql-toolbar button:hover .ql-fill,
                .landing-page-quill .ql-toolbar button.ql-active .ql-fill { fill: #fff; }
                .landing-page-quill .ql-container {
                  background: #131C31;
                  border-color: rgba(255,255,255,0.1) !important;
                  border-radius: 0 0 8px 8px;
                  min-height: 160px;
                }
                .landing-page-quill .ql-editor {
                  color: #fff;
                  min-height: 160px;
                  font-size: 14px;
                }
                .landing-page-quill .ql-editor.ql-blank::before {
                  color: #475569;
                  font-style: normal;
                }
                .landing-page-quill .ql-picker-options {
                  background: #1e2a45;
                  border-color: rgba(255,255,255,0.1) !important;
                }
                .landing-page-quill .ql-picker-item { color: #94a3b8; }
                .landing-page-quill .ql-picker-item:hover { color: #fff; }
              `}</style>
              <ReactQuill
                className="landing-page-quill"
                theme="snow"
                value={form.info}
                onChange={(val) => handleChange("info", val)}
                modules={QUILL_MODULES}
                placeholder="Enter detailed information..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Flyer (jpg, jpeg, png)
            </label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) return;
                handleChange("flyer", file);
              }}
              className="w-full text-sm text-slate-400"
            />
            {form.flyer && (
              <div className="mt-3">
                <img
                  src={typeof form.flyer === "string" ? form.flyer : URL.createObjectURL(form.flyer)}
                  alt="Flyer preview"
                  className="w-32 h-32 object-cover rounded-lg border border-white/10"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Whatsapp Redirection Link
            </label>
            <input
              type="text"
              value={form.link}
              onChange={(e) => handleChange("link", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <LoadScript googleMapsApiKey={serviceKeys.API_KEY} libraries={libraries}>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Address</label>
              <Autocomplete
                onLoad={(auto) => setAutocomplete(auto)}
                onPlaceChanged={() => {
                  if (autocomplete) {
                    const place = autocomplete.getPlace();
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const address = place.formatted_address;
                    handleChange("map_address", address);
                    handleChange("map_latitude", lat);
                    handleChange("map_longitude", lng);
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="Search location"
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 mb-2 text-sm text-white"
                />
              </Autocomplete>

              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "300px" }}
                center={center}
                zoom={12}
                onClick={(e) => {
                  handleChange("map_latitude", e.latLng.lat());
                  handleChange("map_longitude", e.latLng.lng());
                }}
              >
                {form.map_latitude && form.map_longitude && (
                  <Marker
                    position={{
                      lat: Number(form.map_latitude),
                      lng: Number(form.map_longitude),
                    }}
                    draggable
                    onDragEnd={(e) => {
                      handleChange("map_latitude", e.latLng.lat());
                      handleChange("map_longitude", e.latLng.lng());
                    }}
                  />
                )}
              </GoogleMap>
            </div>
          </LoadScript>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition ${
              loading ? "bg-pink-400 cursor-not-allowed" : "bg-pink-600 hover:bg-pink-700"
            }`}
          >
            {loading ? "Saving..." : mode === "edit" ? "Update Landing Page" : "Create Landing Page"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Outer wrapper — only mounts ModalContent when open ──
// This causes ModalContent (and Quill inside it) to fully unmount/remount
// each time the modal opens, so useState lazy initializer always runs fresh.
const LandingPageFormModal = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <ModalContent {...props} />;
};

export default LandingPageFormModal;